import React, { createContext, useContext, useState, useEffect } from 'react';
import Logo from '../assets/images/logo.png';
import LogoRectangular from '../assets/images/logo-text.png';
import LogoStudio from '../assets/images/followr-studio.svg';
import IsologoStudio from '../assets/images/followr-studio-isologo.svg';
import LogoStudioFollowr from '../assets/images/followr-studio-followr.svg';
import { updateAppName } from '../locales';
import { api as Api } from 'api/http';
import { Notification, toast } from 'components/ui';
import { CompanyContext } from './CompanyContext';
import { useWhitelabelDetection } from './WhitelabelDetectionContext';
import { PUBLIC_URL, isPrimaryFollowrHostname, isFollowrStudioHostname } from 'components/ui/utils/constant';
import { isValidUrl } from 'utils/validate';
import { 
  WhitelabelConfigurationError, 
  WhitelabelLoadingScreen, 
  WhitelabelNoPermission 
} from '../components/whitelabel/Components';
import { SessionContext } from './SessionContext';

const WhitelabelThemeContext = createContext();

const isStudioHost = typeof window !== 'undefined' && isFollowrStudioHostname(window.location.hostname);

const defaultTheme = {
  title: 'Followr',
  name: 'Followr',
  logo: isStudioHost ? IsologoStudio : Logo,
  logoRectangular: isStudioHost ? LogoStudio : LogoRectangular,
  logoStudio: isStudioHost ? LogoStudioFollowr : LogoRectangular,
  favicon: 'favicon.ico',
  domain: PUBLIC_URL,
  palette: ['#1a73e8', '#4285f4', '#8ab4f8'],
  headingFont: 'system-font',
  bodyFont: 'system-font',
  supportEmail: 'support@followr.help',
  contactEmail: 'contact@followr.help',
  colors: {
    primary: 'rgba(3, 202, 230, 1)',
    secondary: 'rgba(255, 255, 255, 1)',
    tertiary: 'rgba(55,65,81, 1)',
  },
  fonts: {
    heading: 'Paytone One',
    body: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif, BlinkMacSystemFont, "Helvetica Neue", Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  },
  helpLink: '', 
  privacyPolicyLink: 'https://followr.ai/privacy-policy',
  termsConditionsLink: 'https://followr.ai/terms-conditions',
  cookiePolicyLink: 'https://followr.ai/cookie-policy', 
  user_id: null,
}

// LocalStorage keys
const STORAGE_KEYS = {
  WHITELABEL_ID: 'followr_whitelabel_id',
  IS_WHITELABEL: 'followr_is_whitelabel',
  WHITELABEL_SETTINGS: 'followr_whitelabel_settings'
};



export const useWhitelabel = () => {
  const context = useContext(WhitelabelThemeContext);
  if (!context) {
    throw new Error('useWhitelabel must be used within a WhitelabelThemeProvider');
  }
  return context;
};

export const WhitelabelThemeProvider = ({ children }) => {

  const { mainUser } = useContext(CompanyContext);
  const { user } = useContext(SessionContext);
  const { mustBeWhitelabeled, setWhitelabelUserId } = useWhitelabelDetection();
  const [theme, setTheme] = useState(
    localStorage.getItem(STORAGE_KEYS.WHITELABEL_SETTINGS) 
      ? JSON.parse(localStorage.getItem(STORAGE_KEYS.WHITELABEL_SETTINGS)) 
      : defaultTheme
  );
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [whitelabelId, setWhitelabelId] = useState(null);
  const [hasWhiteLabelAddOn, setHasWhiteLabelAddOn] = useState(false);
  const [shouldShowLoading, setShouldShowLoading] = useState(true);
  const [frontendUrl, setFrontendUrl] = useState('');

  // Function to get font family from font key
  const getFontFamily = (fontKey) => {
    const fontMap = {
      // System fonts
      'system-font': 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif, BlinkMacSystemFont, "Helvetica Neue", Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      
      // Sans-serif fonts
      'roboto-font': 'Roboto, sans-serif',
      'inter-font': 'Inter, sans-serif',
      'poppins-font': 'Poppins, sans-serif',
      'montserrat-font': 'Montserrat, sans-serif',
      'open-sans-font': 'Open Sans, sans-serif',
      'lato-font': 'Lato, sans-serif',
      'raleway-font': 'Raleway, sans-serif',
      'nunito-font': 'Nunito, sans-serif',
      'source-sans-font': 'Source Sans Pro, sans-serif',
      'ubuntu-font': 'Ubuntu, sans-serif',
      'helvetica-font': 'Helvetica, Arial, sans-serif',
      'arial-font': 'Arial, sans-serif',
      'verdana-font': 'Verdana, sans-serif',
      'tahoma-font': 'Tahoma, sans-serif',
      
      // Serif fonts
      'playfair-font': 'Playfair Display, serif',
      'merriweather-font': 'Merriweather, serif',
      'georgia-font': 'Georgia, serif',
      'times-font': 'Times New Roman, serif',
      'garamond-font': 'Garamond, serif',
      'baskerville-font': 'Baskerville, serif',
      
      // Monospace fonts
      'courier-font': 'Courier New, monospace',
      'consolas-font': 'Consolas, monospace',
      'monaco-font': 'Monaco, monospace',
      'source-code-font': 'Source Code Pro, monospace',
      'fira-code-font': 'Fira Code, monospace',
      
      // Display fonts
      'bebas-font': 'Bebas Neue, cursive',
      'pacifico-font': 'Pacifico, cursive',
      'lobster-font': 'Lobster, cursive',
      'comfortaa-font': 'Comfortaa, cursive',
      'paytone-one-font': 'Paytone One, sans-serif',
    };
    return fontMap[fontKey] || fontKey;
  };

  // Save whitelabel ID to localStorage
  const saveWhitelabelId = (id, isWL) => {
    try {
      localStorage.setItem(STORAGE_KEYS.WHITELABEL_ID, id || '');
      localStorage.setItem(STORAGE_KEYS.IS_WHITELABEL, isWL ? 'true' : 'false');
    } catch (error) {
      console.error('Error saving whitelabel ID to localStorage:', error);
    }
  };

  // Function to decode the encoded whitelabel ID
  const decodeWhitelabelId = (encodedId) => {
    try {
      if (!encodedId) return null;
      
      // Decode the base64 string
      const decodedString = atob(encodedId);
      
      // Extract the ID from the format "wl-{id}-{timestamp}"
      const matches = decodedString.match(/^wl-([^-]+)-\d+$/);

      if (matches && matches[1]) {
        return matches[1]; // Return the captured ID
      }
      
      return null;
    } catch (error) {
      console.error('Error decoding whitelabel ID:', error);
      return null;
    }
  };

  // Clear whitelabel theme and reset to default
  const clearWhitelabelTheme = () => {
    setTheme(defaultTheme);
    setWhitelabelId(null);
    setIsWhitelabel(false);
    setWhitelabelUserId(null);

    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEYS.WHITELABEL_ID);
      localStorage.removeItem(STORAGE_KEYS.WHITELABEL_SETTINGS);
      localStorage.removeItem(STORAGE_KEYS.IS_WHITELABEL);
    } catch (error) {
      console.error('Error clearing whitelabel theme:', error);
    }
  };

  // Function to fetch whitelabel theme by ID
  const fetchWhitelabelTheme = async (id) => {
    try {
      if (!id) throw new Error('No whitelabel ID provided');
      if (isPrimaryFollowrHostname(id)) throw new Error('Invalid whitelabel ID / Domain');
      
      setIsLoading(true);
      if (!theme) {
        setShouldShowLoading(true);
      }
      
      const response = await Api.get(`/public/whiteLabels/${id}?include=image.thumbnail,favicon.thumbnail,cover.thumbnail`, {}, { type: 'whitelabel' }).catch(error => {
        // console.error('Error fetching whitelabel theme:', error);
        // if (error?.response?.status === 404) {
          // throw new Error('Invalid whitelabel ID / Domain');
        // }
        return null;
      });
      
      if (response && response.data) {
        const whitelabelData = response.data;

        setWhitelabelUserId(whitelabelData?.user_id || whitelabelData?.user?.id);
        
        // Transform the whitelabel data to theme format
        const newTheme = {
          id: whitelabelData.id,
          user_id: whitelabelData?.user_id || whitelabelData?.user?.id,
          title: whitelabelData.title ?? defaultTheme.title,
          domain: whitelabelData.domain ?? defaultTheme.domain,
          name: whitelabelData.name ?? defaultTheme.name,
          logo: whitelabelData.image?.url ?? defaultTheme.logo,
          logoRectangular: whitelabelData.cover?.url ?? defaultTheme.logoRectangular,
          logoStudio: whitelabelData.cover?.url ?? defaultTheme.logoStudio,
          favicon: whitelabelData.favicon?.url ?? defaultTheme.favicon,
          palette: whitelabelData.palette ?? defaultTheme.palette,
          headingFont: whitelabelData.fonts?.[0] ?? defaultTheme.headingFont,
          bodyFont: whitelabelData.fonts?.[1] ?? defaultTheme.bodyFont,
          supportEmail: whitelabelData.support_email ?? defaultTheme.supportEmail,
          contactEmail: whitelabelData.contact_email ?? defaultTheme.contactEmail,
          colors: {
            primary: whitelabelData.palette?.[0] ?? defaultTheme.colors.primary,
            secondary: whitelabelData.palette?.[1] ?? defaultTheme.colors.secondary,
            tertiary: whitelabelData.palette?.[2] ?? defaultTheme.colors.tertiary,
          },
          fonts: {
            heading: getFontFamily(whitelabelData.fonts?.[0]) ?? defaultTheme.fonts.heading,
            body: getFontFamily(whitelabelData.fonts?.[1]) ?? defaultTheme.fonts.body,
          },
          helpLink: whitelabelData.help_center_link ?? defaultTheme.helpLink,
          privacyPolicyLink: whitelabelData.privacy_terms_link ?? defaultTheme.privacyPolicyLink,
          termsConditionsLink: whitelabelData.terms_conditions_link ?? defaultTheme.termsConditionsLink,
          cookiePolicyLink: whitelabelData.cookie_policy_link ?? defaultTheme.cookiePolicyLink,
        };
        
        setTheme(newTheme);
        setWhitelabelId(whitelabelData.id);
        setIsWhitelabel(true);
        
        // Save ID to localStorage
        saveWhitelabelId(id, true);
        localStorage.setItem(STORAGE_KEYS.WHITELABEL_SETTINGS, JSON.stringify(newTheme));
      } else {
        throw new Error('Invalid whitelabel ID / Domain');
      }
    } catch (error) {
      clearWhitelabelTheme();
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setShouldShowLoading(false);
      }, 2000);
    }
  };
  
  const updateTheme = (newTheme) => {
    const updatedTheme = {
      ...theme,
      ...newTheme
    };
    setTheme(updatedTheme);
  };

  // Check if it's in a whitelabel
  useEffect(() => {
    let hostname = null;
    let whitelabelId = null;
    try {
      hostname = new URL(calculateFrontendUrl())?.hostname;
    } catch (error) {}
    try {
      whitelabelId = calculateWhitelabelId();
    } catch (error) {}
    fetchWhitelabelTheme(whitelabelId || hostname);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    
    const root = document.documentElement;
    
    if (mustBeWhitelabeled && theme?.id) {
      document.title = theme.title;
      
      if (theme.favicon) {
        const existingFavicon = document.querySelector('link[rel="icon"]');
        if (existingFavicon) {
          existingFavicon.href = theme.favicon;
        } else {
          const favicon = document.createElement('link');
          favicon.rel = 'icon';
          favicon.href = theme.favicon;
          document.head.appendChild(favicon);
        }
      }
      
      // Apply colors to CSS variables
      Object.entries(theme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--${key}-color`, value);
      });

      // Apply fonts
      root.style.setProperty('--font-heading', theme.fonts.heading);
      root.style.setProperty('--font-body', theme.fonts.body);

      // Update app name in translations
      updateAppName(theme.title);
    }
  }, [theme, isLoading]);

  useEffect(() => {
    if (!!mainUser?.id) {
      if (mainUser?.subscription?.items?.length > 0) {
        if (
          mainUser?.subscription?.items?.find(item => item.product.name == 'white-label') ||
          !!user?.balance?.white_label_enabled
        ) {
          setHasWhiteLabelAddOn(true);
        }
      }
    } 
  }, [mainUser?.id, mainUser?.subscription?.items?.length, user?.balance?.white_label_enabled]);

  // Function to extract clean URL (without query params)
  const getCleanUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.origin + urlObj.pathname;
    } catch (e) {
      return url?.split('?')?.[0];
    }
  };

  const calculateWhitelabelId = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const encodedWhitelabelId = queryParams.get('wl');
    const decodedId = decodeWhitelabelId(encodedWhitelabelId);
    return decodedId;
  }

  const calculateFrontendUrl = () => {
    // If we have a whitelabel with a domain, use that
    if (isWhitelabel && theme?.domain) {
      return theme.domain;
    }
    
    // Otherwise, use the current window origin
    return window.location.origin;
  }
  
  useEffect(() => {
    setFrontendUrl(getCleanUrl(calculateFrontendUrl()));
  }, [isWhitelabel, theme?.domain]);

  useEffect(() => {
    setTimeout(() => {
      setShouldShowLoading(false);
    }, 2000);
  }, []);

  return (
    <>
      <WhitelabelThemeContext.Provider value={{ 
        theme, 
        updateTheme, 
        whitelabel: theme,
        isWhitelabel,
        isLoading,
        whitelabelId: whitelabelId || theme?.id,
        hasWhiteLabelAddOn,
        clearWhitelabelTheme,
        fetchWhitelabelTheme,
        frontendUrl: frontendUrl ? frontendUrl.replace(/\/$/, '') : window.location.origin,
        availableFonts: Object.keys(getFontFamily).reduce((acc, key) => {
          acc[key] = getFontFamily(key);
          return acc;
        }, {})
      }}>
        {/* Show fullscreen loading when needed */}
        {mustBeWhitelabeled ? (
          <>
            {shouldShowLoading ? (
              <WhitelabelLoadingScreen />
            ) : !theme?.id ? (
              <WhitelabelConfigurationError />
            ) : (<>
              {!!user?.id && theme?.user_id !== mainUser?.id && (
                <WhitelabelNoPermission />
              )}
              {children}
            </>
            )}
          </>
        ) : children}
      </WhitelabelThemeContext.Provider>
    </>
  );
};